import { Suspense, forwardRef, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { FormikProps } from "formik"
import { CreateUpdateUserGroupFormValues } from "../userGroupsTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import {
    CompanyAdminUserGroupsQuery,
    fetchUserGroupForCompany,
    updateUserGroupForCompany,
    UserGroupForCompanyResponse,
} from "../userGroupsApi"
import { CreateUpdateUserGroupForm } from "../components/CreateUpdateUserGroupForm"
import { COMPANY_ADMIN_ORGANIZATION_USER_GROUPS_ROUTE, COMPANY_ADMIN_ROUTE } from "../../../../../../constants/routes"

const selectUserGroup = (data: UserGroupForCompanyResponse) => {
    const userGroup = data.userGroup
    if (!userGroup) return null
    const { id, company, history, ...restValues } = userGroup

    const initialValues = {
        ...restValues,
    }

    return { initialValues, history }
}

const UserGroup = forwardRef<FormikProps<CreateUpdateUserGroupFormValues>>((props, ref) => {
    const { userGroupId } = useParams()
    const navigate = useNavigate()

    const userGroupResult = useApiQuery(fetchUserGroupForCompany, {
        queryName: CompanyAdminUserGroupsQuery.DETAILS,
        dependencies: {
            userGroupId: userGroupId || "",
        },
        enabled: Boolean(userGroupId),
        select: selectUserGroup,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutate: callUpdateUserGroup } = useApiMutation(updateUserGroupForCompany, {
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [CompanyAdminUserGroupsQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(CompanyAdminUserGroupsQuery.DETAILS, { userGroupId: params.userGroupId }),
            })
            navigate(COMPANY_ADMIN_ORGANIZATION_USER_GROUPS_ROUTE)
            if (!data.serviceResponse.displayMessage) return
            enqueueSnackbar({
                variant: "success",
                message: data?.serviceResponse.displayMessage,
            })
        },
    })

    return (
        <>
            <CreateUpdateUserGroupForm
                ref={ref}
                initialValues={userGroupResult.data?.initialValues}
                history={userGroupResult.data?.history}
                onSubmit={(data) => {
                    userGroupId && callUpdateUserGroup({ userGroup: data, userGroupId: userGroupId })
                }}
            />
        </>
    )
})

export function EditUserGroup() {
    const formRef = useRef<FormikProps<CreateUpdateUserGroupFormValues>>(null)

    const handleSaveClick = () => {
        formRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="edit-user-group-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.COMPANY_ADMIN_ORGANIZATION_USER_GROUPS_CREATE}
                    title="Edit user group"
                    rightElement={
                        <LocalizedStrict id="edit-user-group-save-button">
                            <StandardButton type="submit" onClick={handleSaveClick}>
                                Save
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-title">
                            <Breadcrumb>Organization</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-user-groups-title">
                            <BreadcrumbLink to={COMPANY_ADMIN_ORGANIZATION_USER_GROUPS_ROUTE}>
                                User groups
                            </BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-organization-user-group-edit-title">
                            <Breadcrumb>Edit user group</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <UserGroup ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
